import React, { useCallback, useEffect, useState } from "react";
import { Bell, CalendarDays, Check, BellRing } from "lucide-react";
import {
  getEventos,
  marcarDestaqueVisto,
  lembrarDepois,
} from "../../services/eventosStore";
import { formatDatePtBR as formatarData } from "../../utils/formatting";
import { corDaTurma } from "../../utils/coresTurma";
import "./AvisoDestaque.scss";

// "Lembrar depois" deve sumir nesta sessão e reaparecer só quando o usuário
// fechar e abrir a plataforma. O backend não tem conceito de sessão
// (LembrarMaisTarde apenas desmarca o "visto"), então o adiamento por sessão
// vive no sessionStorage — limpo ao fechar a aba/navegador.
const CHAVE_ADIADOS = "avisos_destaque_adiados";

function lerAdiados() {
  try {
    const arr = JSON.parse(sessionStorage.getItem(CHAVE_ADIADOS) || "[]");
    return Array.isArray(arr) ? arr.map(String) : [];
  } catch {
    return [];
  }
}

// Chave do adiamento é por (evento, turma): cada card é independente.
function chaveAdiado(eventoId, turmaId) {
  return `${eventoId}:${turmaId ?? "all"}`;
}

function adiarNaSessao(eventoId, turmaId) {
  const atuais = new Set(lerAdiados());
  atuais.add(chaveAdiado(eventoId, turmaId));
  sessionStorage.setItem(CHAVE_ADIADOS, JSON.stringify([...atuais]));
}

// Um aviso é exibido por (evento × turma do aluno). O mesmo evento publicado em
// duas turmas do aluno gera dois cards independentes, cada um com nome, cor e
// estado (visto/lembrar) da sua turma.
function explodirPorTurma(ev) {
  const turmas =
    Array.isArray(ev?.turmas) && ev.turmas.length
      ? // Destaque não é retroativo: ignora turmas onde o aluno entrou depois da
        // criação do evento (o calendário desse mesmo evento ainda aparece).
        ev.turmas.filter((t) => t?.destaqueElegivel !== false)
      : [null];
  return turmas.map((turma) => ({
    chave: chaveAdiado(ev.id, turma?.id),
    eventoId: ev.id,
    turmaId: turma?.id ?? null,
    destaqueVisto: !!turma?.destaqueVisto,
    titulo: ev.titulo,
    descricao: ev.descricao,
    criadoPorNome: ev.criadoPorNome,
    // Destaque não tem data de evento; mostra a data em que foi criado.
    dataCriacao: ev.criadoEm,
    turmaNome: turma?.complemento
      ? `${turma.nome} - ${turma.complemento}`
      : turma?.nome || "",
    // Mesma cor que o aluno escolheu para a turma na lateral.
    cor: corDaTurma(turma?.id),
    ordenacao: new Date(ev.ultimaAtualizacao || ev.criadoEm || 0).getTime(),
  }));
}

export default function AvisoDestaque({ usuario }) {
  const [avisosVisiveis, setAvisosVisiveis] = useState([]);
  const [indiceAtual, setIndiceAtual] = useState(0);

  const carregar = useCallback(() => {
    // Só alunos (inclui aluno-responsável, que é do tipo "aluno"). Responsável
    // puro não recebe o aviso em destaque.
    const tipo = String(usuario?.tipo || "").toLowerCase();
    if (!usuario || tipo !== "aluno") {
      setAvisosVisiveis([]);
      return;
    }

    // O backend já entrega só os eventos das turmas do usuário, com o estado
    // individual por turma. Explode em um card por turma e filtra os já vistos
    // (por turma) e os adiados nesta sessão (por turma).
    const adiados = new Set(lerAdiados());
    const lista = getEventos()
      .filter((ev) => ev?.destaque === true)
      .flatMap(explodirPorTurma)
      .filter((c) => !c.destaqueVisto)
      .filter((c) => !adiados.has(c.chave))
      .sort((a, b) => b.ordenacao - a.ordenacao);

    setAvisosVisiveis(lista);
    setIndiceAtual(0);
  }, [usuario]);

  useEffect(() => {
    carregar();
    window.addEventListener("app:data-changed", carregar);
    window.addEventListener("eventos:changed", carregar);
    return () => {
      window.removeEventListener("app:data-changed", carregar);
      window.removeEventListener("eventos:changed", carregar);
    };
  }, [carregar]);

  const aviso = avisosVisiveis[indiceAtual];

  // Resolve apenas o card daquela turma (estado é por turma).
  // "visto": destaqueVisto=true no backend para a turma (não reaparece).
  // "adiado": registra lembrar-depois e some nesta sessão (reaparece em nova
  // sessão via guard do sessionStorage).
  async function resolver(card, tipo) {
    if (tipo === "adiado") adiarNaSessao(card.eventoId, card.turmaId);

    const restantes = avisosVisiveis.filter((c) => c.chave !== card.chave);
    setAvisosVisiveis(restantes);
    setIndiceAtual((i) =>
      restantes.length ? Math.min(i, restantes.length - 1) : 0,
    );

    try {
      if (tipo === "visto")
        await marcarDestaqueVisto(card.eventoId, card.turmaId);
      else await lembrarDepois(card.eventoId, card.turmaId);
    } catch {
      // falha de rede não trava a UI; o estado reconcilia no próximo carregar()
    }
  }

  if (!aviso) return null;

  return (
    <div className="aviso-destaque-overlay">
      <div
        className="aviso-destaque-card"
        role="dialog"
        aria-modal="true"
        style={{
          "--aviso-faixa-cor": aviso.cor,
          "--aviso-chip-cor": aviso.cor,
        }}
      >
        <div className="aviso-destaque-faixa" />

        <div className="aviso-destaque-content">
          <div className="aviso-destaque-header">
            <Bell size={22} strokeWidth={2.4} />
            <h2>Aviso em destaque</h2>
          </div>

          <h3 className="aviso-destaque-titulo">{aviso.titulo}</h3>

          {aviso.turmaNome ? (
            <div className="aviso-destaque-chip">{aviso.turmaNome}</div>
          ) : null}

          <div className="aviso-destaque-meta">
            <span className="aviso-destaque-autor">
              <strong>Criado por:</strong> {aviso.criadoPorNome || "—"}
            </span>

            {aviso.dataCriacao ? (
              <span className="aviso-destaque-data">
                <CalendarDays size={15} />
                {formatarData(aviso.dataCriacao)}
              </span>
            ) : null}
          </div>

          <p className="aviso-destaque-descricao">{aviso.descricao}</p>

          <div className="aviso-destaque-actions">
            <button
              type="button"
              className="btn-aviso btn-aviso-outline"
              onClick={() => resolver(aviso, "visto")}
            >
              <Check size={18} />
              Marcar como visto
            </button>

            <button
              type="button"
              className="btn-aviso btn-aviso-outline"
              onClick={() => resolver(aviso, "adiado")}
            >
              <BellRing size={18} />
              Me lembre depois
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
